// Regras puras do formulário de Checkout: máscaras, validações, pré-preenchimento
// e montagem do payload. Os campos exibem máscara, mas o buyProductHandler
// recebe só dígitos (CPF, telefone, CEP e cartão).

export const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

// 000.000.000-00, aplicada conforme a digitação.
export const formatCpf = (value) => {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

// Dígitos verificadores do CPF (a Pagar.me recusa CPF inválido).
export const isValidCpf = (value) => {
  const d = onlyDigits(value);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const digit = (len) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(d[i]) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return digit(9) === Number(d[9]) && digit(10) === Number(d[10]);
};

// (11) 99999-9999 ou (11) 9999-9999, aplicada conforme a digitação.
export const formatPhone = (value) => {
  const d = onlyDigits(value).slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  const rest = d.slice(2);
  if (rest.length <= 4) return `(${d.slice(0, 2)}) ${rest}`;
  const split = rest.length === 9 ? 5 : 4;
  return `(${d.slice(0, 2)}) ${rest.slice(0, split)}-${rest.slice(split)}`;
};

export const isValidPhone = (value) => [10, 11].includes(onlyDigits(value).length);

// Celular gravado em formatos variados: "55 11 999999999" (webhook Pagar.me),
// "(11)99999-9999" (perfil), "+5511999999999"... Descarta o DDI 55.
export const normalizeStoredPhone = (value) => {
  let d = onlyDigits(value);
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  return isValidPhone(d) ? formatPhone(d) : '';
};

// 00000-000
export const formatCep = (value) => {
  const d = onlyDigits(value).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
};

// Grupos de 4 dígitos para leitura.
export const formatCardNumber = (value) =>
  onlyDigits(value).slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');

// MM/AA
export const formatExpiration = (value) => {
  const d = onlyDigits(value).slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
};

export const validateExpiration = (value, now = new Date()) => {
  if (!/^\d{2}\/\d{2}$/.test(value || '')) return 'Use o formato MM/AA.';
  const [month, year] = value.split('/').map(Number);
  if (month < 1 || month > 12) return 'Mês inválido.';
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;
  if (year < currentYear || (year === currentYear && month < currentMonth)) return 'Cartão vencido.';
  return true;
};

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const formatBRL = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const [int, cents] = n.toFixed(2).split('.');
  return `R$ ${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${cents}`;
};

// Dados para pré-preencher o checkout. Prioridade: cadastro da aluna logada
// (`users/{uid}`) > parâmetros da URL (a vitrine manda name/email/phone_number/doc).
export const buildCheckoutPrefill = ({ user, authEmail, params } = {}) => {
  const cpf = onlyDigits(user?.CPF || user?.Doc || params?.doc);
  return {
    name: String(user?.Nome_Completo || params?.name || '').trim(),
    email: String(user?.Email || authEmail || params?.email || '').trim().toLowerCase(),
    document: cpf.length === 11 ? formatCpf(cpf) : '',
    phone: normalizeStoredPhone(user?.Celular || params?.phone_number),
  };
};

// Valores do formulário (com máscara) -> payload do buyProductHandler.
export const buildCheckoutPayload = (values) => {
  const { customer = {}, address, payment } = values;
  const phone = onlyDigits(customer.phone);
  return {
    customer: {
      name: String(customer.name || '').trim(),
      email: String(customer.email || '').trim().toLowerCase(),
      document: onlyDigits(customer.document),
      areaCode: phone.slice(0, 2),
      phone: phone.slice(2),
    },
    ...(address && {
      address: { ...address, zipCode: onlyDigits(address.zipCode), country: 'BR' },
    }),
    ...(payment && {
      // Na web o <select> de parcelas devolve texto ("3").
      payment: {
        ...payment,
        cardNumber: onlyDigits(payment.cardNumber),
        paymentFraction: Number(payment.paymentFraction) || 1,
      },
    }),
  };
};
